import type {BaseLogger} from '@wix/document-services-types'
import {taskWithRetriesAndInfo} from '../taskWithRetries'
import _ from 'lodash'
import type {Attempt} from '../function'

export interface ShouldRetry {
    shouldRetry: boolean
    reason?: string
}
export type CheckIfShouldRetryFunction<T, E extends Error> = (error: E | Awaited<T>) => ShouldRetry
export interface RetryTaskAndReportParams<T, E extends Error> {
    task(): Promise<T>
    checkIfShouldRetry: CheckIfShouldRetryFunction<T, E>
    interactionName: string
    maxRetries?: number
    waitInterval?: number
    logger?: BaseLogger
}

const getEventualSuccess = <T, E extends Error>(result: Attempt<T, E>) => {
    if (result.error || _.get(result, ['value', 'success']) === false) {
        return false
    }
    return true
}

export const retryTaskAndReport = async <T, E extends Error>(
    retryTaskAndReportParams: RetryTaskAndReportParams<T, E>
): Promise<T> => {
    const {task, checkIfShouldRetry, interactionName, maxRetries, waitInterval, logger} = retryTaskAndReportParams
    const eventGuid = _.random(10000, true)
    logger?.interactionStarted(interactionName, {eventGuid})
    const {result, retries, reason} = await taskWithRetriesAndInfo(task, checkIfShouldRetry, maxRetries, waitInterval)
    const status = _.get(result, ['value', 'status'])
    const tags = {
        [interactionName]: true,
        retries,
        eventualSuccess: getEventualSuccess(result),
        reason,
        ...(status !== undefined && {status})
    }
    logger?.interactionEnded(interactionName, {tags, eventGuid})
    if (result.didThrow) {
        throw result.error
    }
    return result.value
}
